/**
 * 视频生成服务 - 基于火山方舟（Volcengine）视频生成 API
 * 文档: https://www.volcengine.com/docs/82379/1366799
 * 创建任务: https://www.volcengine.com/docs/82379/1520757
 * 查询任务: https://www.volcengine.com/docs/82379/1521309
 * 使用后台配置的模型，如 doubao-seedance-1-5-pro-251215
 */

import { alibabaAI, modelAPI } from './api';

export interface VideoModelConfig {
  id: string;
  name: string;
  type: string;
  provider: string;
  api_key: string;
  base_url?: string;
  model_name: string;
  is_active: boolean;
}

export interface CreateVideoTaskOptions {
  /** 文本提示词，必填 */
  prompt: string;
  /** 视频时长（秒），如 5、10 */
  duration: number;
  /** 宽高比，如 "9:16"、"16:9"、"1:1" */
  ratio: string;
  /** 可选：首帧参考图 URL（支持 data URL 或 https） */
  imageUrl?: string | null;
  /** 可选：尾帧参考图 URL（首尾帧能力，支持 data URL 或 https） */
  lastFrameUrl?: string | null;
  /** 是否固定镜头，默认 false */
  camera_fixed?: boolean;
}

export interface VideoTaskResult {
  id: string;
  status?: string;
  content?: {
    video_url?: string;
    url?: string;
    videos?: Array<{ url?: string }>;
  };
}

/** 从查询任务响应中解析视频 URL（兼容多种 API 返回结构） */
export function getVideoUrlFromTaskResult(task: VideoTaskResult | null): string | undefined {
  if (!task?.content) return undefined;
  const c = task.content as Record<string, unknown>;
  if (typeof c.video_url === 'string') return c.video_url;
  if (typeof c.url === 'string') return c.url;
  const arr = c.videos as Array<{ url?: string }> | undefined;
  if (Array.isArray(arr) && arr[0]?.url) return arr[0].url;
  // content 可能为数组，如 [{ type: 'video', url: '...' }]
  const contentArr = c.output as Array<{ url?: string; video_url?: string }> | undefined;
  if (Array.isArray(contentArr) && contentArr[0]) {
    const first = contentArr[0];
    if (typeof first.video_url === 'string') return first.video_url;
    if (typeof first.url === 'string') return first.url;
  }
  return undefined;
}

const DEFAULT_BASE_URL = 'https://ark.cn-beijing.volces.com/api/v3';

/** 火山方舟「内容生成」任务仅支持 Seedance 等视频模型，不支持 doubao-seed-* 对话模型 */
export function isArkVideoGenerationModel(modelName?: string): boolean {
  const n = (modelName || '').toLowerCase();
  if (!n) return false;
  if (n.includes('seedance')) return true;
  if (/doubao-seed-\d/.test(n)) return false;
  return true;
}

function parseArkApiErrorMessage(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const o = err as Record<string, unknown>;
  if (typeof o.message === 'string' && o.message) return o.message;
  const nested = o.error;
  if (nested && typeof nested === 'object') {
    const msg = (nested as { message?: string }).message;
    if (typeof msg === 'string' && msg) return msg;
  }
  if (typeof o.error === 'string' && o.error) return o.error;
  if (typeof o.msg === 'string' && o.msg) return o.msg;
  return undefined;
}

function toVideoGenerationErrorMessage(rawMsg: string | undefined, status: number): string {
  const msg = rawMsg || (status === 400 ? '参数不合法（视频时长仅支持 5 秒或 10 秒）' : `请求失败 ${status}`);
  if (/does not support content generation/i.test(msg)) {
    return '当前模型不支持视频生成，请在后台启用 Seedance 视频模型（如 doubao-seedance-1-5-pro），勿使用 Seed 对话模型';
  }
  return msg;
}

async function ensureMinImageSize(imageUrl: string, minSize: number = 300): Promise<string> {
  // 仅处理 data URL，本地上传图片走这个分支；远程 URL 原样透传
  if (!imageUrl.startsWith('data:')) return imageUrl;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      if (width >= minSize && height >= minSize) {
        resolve(imageUrl);
        return;
      }

      const scale = Math.max(minSize / width, minSize / height);
      const targetWidth = Math.max(minSize, Math.ceil(width * scale));
      const targetHeight = Math.max(minSize, Math.ceil(height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(imageUrl);
        return;
      }

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(imageUrl);
    img.src = imageUrl;
  });
}

/**
 * 从后台获取启用的视频模型配置（当前启用供应商）
 */
export async function getVideoModelConfig(options?: { forImageToVideo?: boolean }): Promise<VideoModelConfig | null> {
  try {
    const models = await modelAPI.getAllModels();
    const activeVideo = models
      .filter((model) => model.type === 'video' && model.is_active)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    if (activeVideo.length === 0) return null;

    // 阿里：按文生/图生优先匹配模型名
    const alibabaModels = activeVideo.filter((m) => m.provider === 'alibaba');
    if (alibabaModels.length > 0) {
      if (options?.forImageToVideo) {
        return (
          (alibabaModels.find((m) => /i2v/i.test(m.model_name)) as VideoModelConfig) ||
          (alibabaModels[0] as VideoModelConfig)
        );
      }
      return (
        (alibabaModels.find((m) => /t2v/i.test(m.model_name)) as VideoModelConfig) ||
        (alibabaModels[0] as VideoModelConfig)
      );
    }

    // 火山引擎：需为 Seedance 等视频生成模型
    const doubao = activeVideo.find(
      (m) => m.provider === 'doubao' && isArkVideoGenerationModel(m.model_name)
    );
    if (doubao) return doubao as VideoModelConfig;

    // 其它供应商（如钱多多）若配置了 video 则直接返回
    return activeVideo[0] as VideoModelConfig;
  } catch (error) {
    console.error('获取视频模型配置失败:', error);
    return null;
  }
}

/**
 * 创建视频生成任务（火山方舟 / 阿里 DashScope 代理）
 */
export async function createVideoTask(
  config: VideoModelConfig,
  options: CreateVideoTaskOptions
): Promise<VideoTaskResult | null> {
  const { prompt, duration, ratio, imageUrl, lastFrameUrl, camera_fixed = false } = options;

  // 阿里：走后端代理
  if (config.provider === 'alibaba') {
    const durationNum = typeof duration === 'number' ? duration : parseInt(String(duration), 10) || 5;
    let firstFrame = imageUrl || null;
    if (firstFrame && firstFrame.startsWith('data:')) {
      firstFrame = await ensureMinImageSize(firstFrame, 300);
    }
    const data = await alibabaAI.createVideo({
      prompt: prompt.trim(),
      imageUrl: firstFrame,
      duration: durationNum,
      ratio,
      resolution: '720P',
      model_id: config.id,
    });
    return { id: data.id, status: data.status || 'queued' };
  }

  if (!isArkVideoGenerationModel(config.model_name)) {
    throw new Error(
      '当前配置的是对话模型，无法生成视频。请在后台启用视频生成模型（如火山 Seedance 或阿里 wan 系列）'
    );
  }
  const baseUrl = (config.base_url || DEFAULT_BASE_URL).replace(/\/$/, '');
  const url = `${baseUrl}/contents/generations/tasks`;

  // 火山方舟 Seedance 仅支持 5、10 秒，超出则取最接近值
  const allowedDurations = [5, 10];
  const durationNum = typeof duration === 'number' ? duration : parseInt(String(duration), 10) || 5;
  const apiDuration = allowedDurations.includes(durationNum)
    ? durationNum
    : allowedDurations.reduce((prev, curr) =>
        Math.abs(curr - durationNum) < Math.abs(prev - durationNum) ? curr : prev
      );

  const textWithParams = `${prompt.trim()} --ratio ${ratio} --dur ${apiDuration}`;

  const content: Array<{ type: string; text?: string; image_url?: { url: string }; role?: string }> = [];

  if (imageUrl && (imageUrl.startsWith('data:') || imageUrl.startsWith('http'))) {
    const normalizedFirstFrame = await ensureMinImageSize(imageUrl, 300);
    content.push({ type: 'image_url', image_url: { url: normalizedFirstFrame }, role: 'first_frame' });
  }
  if (lastFrameUrl && (lastFrameUrl.startsWith('data:') || lastFrameUrl.startsWith('http'))) {
    const normalizedLastFrame = await ensureMinImageSize(lastFrameUrl, 300);
    content.push({ type: 'image_url', image_url: { url: normalizedLastFrame }, role: 'last_frame' });
  }
  content.push({ type: 'text', text: textWithParams });

  const body: Record<string, unknown> = {
    model: config.model_name,
    content,
    camera_fixed,
    watermark: true,
  };

  body.duration = apiDuration;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.api_key}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const rawMsg = parseArkApiErrorMessage(err);
      throw new Error(toVideoGenerationErrorMessage(rawMsg, response.status));
    }

    const data = (await response.json()) as VideoTaskResult;
    return data;
  } catch (e) {
    console.error('创建视频任务失败:', e);
    throw e;
  }
}

/**
 * 查询视频生成任务状态
 * 状态: queued | running | succeeded | failed | expired
 */
export async function getVideoTaskStatus(
  config: VideoModelConfig,
  taskId: string
): Promise<VideoTaskResult | null> {
  if (config.provider === 'alibaba') {
    return (await alibabaAI.getVideoTask(taskId)) as VideoTaskResult;
  }

  const baseUrl = (config.base_url || DEFAULT_BASE_URL).replace(/\/$/, '');
  const url = `${baseUrl}/contents/generations/tasks/${taskId}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.api_key}`,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || `查询失败 ${response.status}`);
    }

    const data = (await response.json()) as VideoTaskResult;
    return data;
  } catch (e) {
    console.error('查询视频任务状态失败:', e);
    throw e;
  }
}

